import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Typography, Button, BaseIcon, Tooltip, Spinner } from '@scale/scaleui';
import { ResumeScreenFlow } from 'react-scale/components/GrowthCenter/ResumeScreening/ResumeScreenFlow';
import {
  getJobStages,
  getAllGreenhouseJobs,
  createJobConfig,
  updateJobConfig,
  listJobConfigs,
} from 'react-scale/corp/lib/api/growth';
import { GrowthCenterResumeScreeningTestModal } from 'react-scale/components/GrowthCenter/ResumeScreening/GrowthCenterResumeScreeningTestModal';
import { ResumeScreenDetailPanel } from 'react-scale/components/GrowthCenter/ResumeScreening/ResumeScreenDetailPanel';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  ResumeScreeningConfig,
  ResumeScreeningConfigStatus,
  GreenhouseActionType,
  CreateJobConfigFields,
  UpdateJobConfigFields,
  GreenhouseAction,
  MoveStageGreenhouseAction,
  EditGreenhouseTagsAction,
  NextResumeScreeningAction,
  PodType,
  ScreenType,
  ScreenFilter,
  GROWTH_DEFAULT_RECRUITER_ID,
} from '@scale/shared-types';
import { CreateResumeScreeningSteps } from '../constants';
import SAlert from 'react-s-alert';
import { useParams } from 'react-router-dom';
import Link from '@scale/frontend-lib/link';
import { Routes } from 'react-scale/components/GrowthCenter/urls';
import _ from 'lodash';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';

const generateDraftConfig = (): ResumeScreeningConfig => {
  return {
    _id: '',
    name: '',
    greenhouseJobId: '',
    greenhouseJobName: '',
    assistantDescription: '',
    questionsToAskGPT: [''],
    resumeReviewStageId: '',
    retryGPTTimes: 1,
    onPassActions: [],
    onUnsupportedFileActions: [],
    onFailActions: [],
    resumeScreeningTestSet: [],
    createdBy: '',
    createdAt: new Date(),
    status: ResumeScreeningConfigStatus.ACTIVE,
    type: ScreenType.Greenhouse,
  };
};

const ResumeScreenCreationOrUpdate: React.FC = () => {
  const { jobId } = useParams<{ jobId: string }>();

  const isRecruitingScreeningEnabled = useUnleashFeatureFlag(
    'growth-resume-screen-recruiting',
    {},
    false,
  );

  const queryClient = useQueryClient();
  const [jobStages, setJobStages] = useState<Record<string, string>>({});
  const [loadingJobStages, setLoadingJobStages] = useState<boolean>(false);
  const [allJobConfigs, setAllJobConfigsState] = useState<ResumeScreeningConfig[]>([]);
  const [selectedJobConfig, setSelectedJobConfig] = useState<ResumeScreeningConfig>(); // Initial selected config
  const [activeJobConfig, setActiveJobConfig] = useState<ResumeScreeningConfig>(
    generateDraftConfig(),
  ); // Currently selected config
  const [activeDetailPanel, setActiveDetailPanel] = useState<CreateResumeScreeningSteps>(
    CreateResumeScreeningSteps.ImportGreenhouseJob,
  );
  const [loadingFlowDiagram, setLoadingFlowDiagram] = useState<boolean>(false);
  const [testResumeScreeningDialogOpen, setTestResumeScreeningDialogOpen] =
    useState<boolean>(false);
  const allJobConfigsRef = useRef(allJobConfigs);

  const { data: greenhouseJobs, isLoading: loadingGreenhouseJobs } = useQuery(
    ['getAllGreenhouseJobs', isRecruitingScreeningEnabled, activeJobConfig.type],
    () =>
      getAllGreenhouseJobs(
        isRecruitingScreeningEnabled && activeJobConfig.type === ScreenType.RecruitingGreenhouse
          ? ScreenFilter.Recruiting
          : ScreenFilter.Growth,
      ),
    {
      refetchOnWindowFocus: false,
    },
  );

  const { isLoading: isListJobConfigsFetching } = useQuery(
    ['listJobConfigs', isRecruitingScreeningEnabled],
    () => listJobConfigs(!isRecruitingScreeningEnabled ? ScreenFilter.Growth : undefined),
    {
      refetchOnWindowFocus: false,
      onSuccess: data => {
        setAllJobConfigsState(data);
      },
    },
  );

  const createMutation = useMutation(
    (fields: CreateJobConfigFields) => {
      return createJobConfig(fields);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['listJobConfigs', isRecruitingScreeningEnabled]);
      },
    },
  );
  const updateMutation = useMutation(
    (fields: UpdateJobConfigFields) => {
      return updateJobConfig(fields);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['listJobConfigs', isRecruitingScreeningEnabled]);
      },
    },
  );

  const openJobConfigTestDialog = () => {
    setTestResumeScreeningDialogOpen(true);
  };

  const closeJobConfigTestDialog = () => {
    setTestResumeScreeningDialogOpen(false);
  };

  const updateAllJobConfigs = (updatedConfig: ResumeScreeningConfig) => {
    const configExists = allJobConfigs.some(jobConfig => jobConfig._id === updatedConfig._id);
    let updatedAllConfigs;
    if (configExists) {
      updatedAllConfigs = allJobConfigs.map(jobConfig => {
        if (jobConfig._id === updatedConfig._id) {
          return updatedConfig;
        }
        return jobConfig;
      });
    } else {
      updatedAllConfigs = [...allJobConfigs, updatedConfig];
    }

    setAllJobConfigsState(updatedAllConfigs);
    queryClient.setQueryData(['listJobConfigs', isRecruitingScreeningEnabled], updatedAllConfigs);
  };

  useEffect(() => {
    allJobConfigsRef.current = allJobConfigs;
  }, [allJobConfigs]);

  useEffect(() => {
    const selectedJobConfig = allJobConfigsRef.current?.find(jobConfig => jobConfig._id === jobId);
    if (selectedJobConfig) {
      setSelectedJobConfig(selectedJobConfig);
      setActiveJobConfig(selectedJobConfig);
    } else {
      // Create a new job config if the jobId is not found
      const draftJobConfig = generateDraftConfig();
      setSelectedJobConfig(draftJobConfig);
      setActiveJobConfig(draftJobConfig);
      setAllJobConfigsState([...allJobConfigsRef.current, draftJobConfig]);
    }
  }, [jobId, isListJobConfigsFetching]);

  useEffect(() => {
    const getJobStagesHelper = async (jobId: string) => {
      setLoadingJobStages(true);
      const jobStages = await getJobStages(jobId);
      setJobStages(jobStages);
      setLoadingJobStages(false);
    };
    if (
      (activeJobConfig.type === ScreenType.Greenhouse ||
        activeJobConfig.type === ScreenType.RecruitingGreenhouse) &&
      activeJobConfig?.greenhouseJobId
    ) {
      getJobStagesHelper(activeJobConfig.greenhouseJobId);
    }
  }, [jobId, activeJobConfig?.greenhouseJobId]);

  const formatAction = (action: GreenhouseAction, jobConfig: ResumeScreeningConfig) => {
    if (action.type === GreenhouseActionType.MoveStage) {
      const moveAction = action as MoveStageGreenhouseAction;
      const newMoveAction = {
        type: action.type,
        onBehalfOfDefault: moveAction.onBehalfOfDefault || GROWTH_DEFAULT_RECRUITER_ID,
        fromStageId: jobConfig.resumeReviewStageId || moveAction.fromStageId,
        toStageId: moveAction.toStageId,
      };
      return newMoveAction;
    } else if (action.type === GreenhouseActionType.EditTags) {
      const editTagsAction = action as EditGreenhouseTagsAction;
      const newEditAction = {
        type: action.type,
        onBehalfOfDefault: editTagsAction.onBehalfOfDefault || GROWTH_DEFAULT_RECRUITER_ID,
        tagsToAdd: editTagsAction.tagsToAdd,
        tagsToRemove: editTagsAction.tagsToRemove,
      };
      return newEditAction;
    } else if (action.type === GreenhouseActionType.NextResumeScreening) {
      const nextResumeScreenAction = action as NextResumeScreeningAction;
      const newNextResumeScreenAction = {
        type: action.type,
        onBehalfOfDefault: nextResumeScreenAction.onBehalfOfDefault || GROWTH_DEFAULT_RECRUITER_ID,
        nextResumeScreeningConfigId: nextResumeScreenAction.nextResumeScreeningConfigId,
      };
      return newNextResumeScreenAction;
    }
  };

  const formatConfig = (jobConfig: ResumeScreeningConfig, isChainedConfig?: boolean) => {
    const passActions =
      (jobConfig.onPassActions?.map(action =>
        formatAction(action, jobConfig),
      ) as GreenhouseAction[]) || [];
    const failActions =
      (jobConfig.onFailActions?.map(action =>
        formatAction(action, jobConfig),
      ) as GreenhouseAction[]) || [];
    const needsHumanReviewActions =
      (jobConfig.onUnsupportedFileActions?.map(action =>
        formatAction(action, jobConfig),
      ) as GreenhouseAction[]) || [];
    let formattedConfig: any = {
      greenhouseJobId: jobConfig.greenhouseJobId || 'ocp-job-id', // placeholder for OCP
      name: jobConfig.name,
      greenhouseJobName: jobConfig.greenhouseJobName || 'ocp-job-name', // placeholder for OCP
      resumeReviewStageId: jobConfig.resumeReviewStageId || 'ocp-review-stage-id', // placeholder for OCP
      questionsToAskGPT: jobConfig.questionsToAskGPT,
      assistantDescription: jobConfig.assistantDescription,
      retryGPTTimes: !isNaN(Number(jobConfig.retryGPTTimes)) ? Number(jobConfig.retryGPTTimes) : 1,
      onPassActions: passActions,
      onFailActions: failActions,
      onUnsupportedFileActions: needsHumanReviewActions,
      requiresAllQuestionsPassed: jobConfig.requiresAllQuestionsPassed,
      rootConfig: !isChainedConfig,
      podType: jobConfig.podType ?? PodType.NotSet,
      type: jobConfig.type,
    };
    // If the job config is a new config, the resume screening test set and tested fields should be included
    if (!jobConfig._id) {
      formattedConfig = {
        ...formattedConfig,
        resumeScreeningTestSet: jobConfig.resumeScreeningTestSet?.map(item => _.omit(item, '_id')),
        tested: isJobTested,
      };
    }
    if (jobConfig.slackChannel) {
      formattedConfig = { ...formattedConfig, slackChannel: jobConfig.slackChannel };
    }
    return formattedConfig;
  };

  const validateConfig = (
    jobConfig: ResumeScreeningConfig,
  ): { success: boolean; message: string } => {
    const reviewStageId = jobConfig.resumeReviewStageId;
    const passActions = jobConfig.onPassActions;
    const unsupportedFileActions = jobConfig.onUnsupportedFileActions;
    const failActions = jobConfig.onFailActions;

    // If the job config is an OCP screening config, no further action validation is needed
    if (jobConfig.type === ScreenType.OCP) {
      return {
        success: true,
        message: 'Successfully validated resume screening config',
      };
    }

    // Pass actions must include a move stage action and must not direct to the review stage
    const passActionsMoveStage = passActions.find(
      action => action.type === GreenhouseActionType.MoveStage,
    ) as MoveStageGreenhouseAction;
    if (!passActionsMoveStage) {
      return {
        success: false,
        message: 'Pass actions must include a move stage action',
      };
    } else if (passActionsMoveStage.toStageId === reviewStageId) {
      return {
        success: false,
        message: 'Pass actions must not direct to the review stage',
      };
    }

    // Unsupported file actions must include a move stage action
    const unsupportedFileActionsMoveStage = unsupportedFileActions.find(
      action => action.type === GreenhouseActionType.MoveStage,
    ) as MoveStageGreenhouseAction;
    if (!unsupportedFileActionsMoveStage) {
      return {
        success: false,
        message: 'Human review actions must include a move stage action',
      };
    } else if (unsupportedFileActionsMoveStage.toStageId === reviewStageId) {
      return {
        success: false,
        message: 'Human review actions must not direct to the review stage',
      };
    }

    // Fail actions must include a move stage action OR a next resume screening action, but not both
    const failActionsHaveMoveStage = failActions.some(
      action => action.type === GreenhouseActionType.MoveStage,
    );
    const failActionsHaveNextResumeScreening = failActions.some(
      action => action.type === GreenhouseActionType.NextResumeScreening,
    );
    // Checking if both are true or both are false
    if (failActionsHaveMoveStage === failActionsHaveNextResumeScreening) {
      return {
        success: false,
        message:
          'Fail actions must include either a move stage action or a next resume screening action, but not both',
      };
    } else if (failActionsHaveMoveStage) {
      const failActionsMoveStage = failActions.find(
        action => action.type === GreenhouseActionType.MoveStage,
      ) as MoveStageGreenhouseAction;
      if (failActionsMoveStage.toStageId === reviewStageId) {
        return {
          success: false,
          message: 'Fail actions must not direct to the review stage',
        };
      }
    }

    return {
      success: true,
      message: 'Successfully validated resume screening config',
    };
  };

  const createNewConfigAndUpdate = (jobConfig: ResumeScreeningConfig) => {
    // Create new job config for active job config
    const formattedConfig = formatConfig(jobConfig) as CreateJobConfigFields;
    const validationResult = validateConfig(jobConfig);

    if (!validationResult.success) {
      SAlert.error(`Error creating resume screening config: ${validationResult.message}`);
      setLoadingFlowDiagram(false);
      return;
    }

    setLoadingFlowDiagram(true);
    createMutation.mutate(formattedConfig, {
      onError: (error: any) => {
        SAlert.error(`Error creating resume screening config: ${error.response.message.message}`);
      },
      onSuccess: (res: any) => {
        // Update the id of the selected job config to the new id
        const updatedConfig = { ...jobConfig, _id: res.jobId };
        setActiveJobConfig(updatedConfig);
        setSelectedJobConfig(updatedConfig);
        allJobConfigs.push(updatedConfig);
        updateAllJobConfigs(updatedConfig);
        processUpdates(updatedConfig._id, true);
      },
    });
  };

  // Process updates for the current job config and all subsequent chained job configs
  const processUpdates = async (jobConfigId: string, isCreationFlow: boolean = false) => {
    setLoadingFlowDiagram(true);
    let jobConfig = allJobConfigs.find(jobConfig => jobConfig._id === jobConfigId);
    if (!jobConfig) {
      SAlert.error('Error: No job config found');
      return;
    }

    if (isCreationFlow) {
      // Skip the first job config since it was just created
      const nextResumeScreenAction = jobConfig.onFailActions?.find(
        action => action.type === GreenhouseActionType.NextResumeScreening,
      ) as NextResumeScreeningAction;
      if (nextResumeScreenAction && nextResumeScreenAction.nextResumeScreeningConfigId) {
        jobConfig = allJobConfigs.find(
          jobConfig => jobConfig._id === nextResumeScreenAction.nextResumeScreeningConfigId,
        );
      } else {
        jobConfig = undefined;
        setLoadingFlowDiagram(false);
        SAlert.success(`Resume screening pipeline created successfully`);
        return;
      }
    }

    // Recursively collect all job configs in the chained flow
    const configsToUpdate = [];
    while (jobConfig) {
      configsToUpdate.push(jobConfig);
      const nextResumeScreenAction = jobConfig.onFailActions.find(
        action => action.type === GreenhouseActionType.NextResumeScreening,
      ) as NextResumeScreeningAction;
      if (nextResumeScreenAction && nextResumeScreenAction.nextResumeScreeningConfigId) {
        jobConfig = allJobConfigs.find(
          jobConfig => jobConfig._id === nextResumeScreenAction.nextResumeScreeningConfigId,
        );
      } else {
        jobConfig = undefined;
      }
    }

    try {
      for (const config of configsToUpdate) {
        const isChainedConfig = config._id !== jobConfigId;
        const formattedConfig = formatConfig(config, isChainedConfig);

        const validationResult = validateConfig(config);
        if (!validationResult.success) {
          SAlert.error(`Error: ${validationResult.message}`);
          setLoadingFlowDiagram(false);
          return;
        }

        await updateMutation.mutate(
          { ...formattedConfig, jobId: config._id },
          {
            onError: (error: any) => {
              SAlert.error(
                `Error updating config ${config.name}: ${
                  error.response?.message?.message || 'An unknown error occurred'
                }`,
              );
            },
            onSuccess: () => {
              setLoadingFlowDiagram(false);
            },
          },
        );
      }
      SAlert.success(
        `Resume screening pipeline ${isCreationFlow ? 'created' : 'updated'} successfully`,
      );
    } catch (error) {
      SAlert.error(
        `Error during the update process: ${
          error.response?.message?.message || 'An unknown error occurred'
        }`,
      );
    }
  };

  const isJobTested: boolean = useMemo(() => {
    return !!(
      activeJobConfig &&
      activeJobConfig.resumeScreeningTestSet &&
      activeJobConfig.resumeScreeningTestSet.length >= 10 &&
      activeJobConfig.tested
    );
  }, [activeJobConfig.tested, activeJobConfig.resumeScreeningTestSet]);

  const onSave = () => {
    // Create new job config for active job config
    const newSelectedJobConfig = allJobConfigs.find(
      jobConfig => jobConfig._id === selectedJobConfig?._id,
    );
    if (!newSelectedJobConfig) {
      SAlert.error('Error: No job config found');
      return;
    }
    if (!newSelectedJobConfig._id) {
      createNewConfigAndUpdate(newSelectedJobConfig);
    } else {
      processUpdates(newSelectedJobConfig._id);
    }
  };

  if (!selectedJobConfig || !allJobConfigs || isListJobConfigsFetching) {
    return (
      <div className="flex flex-grow h-full w-full justify-center items-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="scaleui scale-tailwind flex flex-col h-full w-full">
      <div className="flex flex-col bg-neutral-0 border-b border-b-neutral-200 border-b-2 justify-between p-1">
        <Button className="w-fit bg-transparent shadow-none" variant="neutral">
          <Link href={`${Routes.ResumeScreening}`} as={`/corp${Routes.ResumeScreening}`}>
            <a className="flex flex-row items-center" rel="noopener noreferrer">
              <BaseIcon contrastText edge="start" icon={['fas', 'arrow-left']} variant="neutral" />
              Exit
            </a>
          </Link>
        </Button>
        <div className="flex flex-row justify-between">
          <div className="flex flex-col">
            <Typography.Paragraph1 className="text-neutral-900 ml-2 font-medium">
              {selectedJobConfig._id ? 'Update Resume Screening' : 'Create Resume Screening'}
            </Typography.Paragraph1>
            <Typography.Paragraph2 className="text-neutral-800 px-2 py-1">
              Automate resume screening using an LLM
            </Typography.Paragraph2>
            {selectedJobConfig._id && (
              <Typography.Paragraph3 className="text-neutral-800 px-2 mb-2">
                ID: {selectedJobConfig._id}
              </Typography.Paragraph3>
            )}
          </div>
          <div className="flex flex-col justify-end items-end">
            <Tooltip
              side="top"
              target={
                <div className="flex justify-end">
                  <Button
                    className="mr-2"
                    variant="primary"
                    disabled={!isJobTested}
                    onClick={onSave}
                  >
                    Save
                  </Button>
                </div>
              }
              text={
                !isJobTested &&
                'Please add at least 10 resumes in your test set and ensure at least 80% accuracy.'
              }
            />
            {!isJobTested && (
              <div className="text-xs text-danger-500 px-2 py-1">
                Please add at least 10 resumes in your test set and ensure at least 80% accuracy.
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="flex flex-row overflow-y-auto h-full">
        <div className="flex flex-col w-[60%]">
          <ResumeScreenFlow
            selectedJobConfigId={selectedJobConfig._id}
            allJobConfigs={allJobConfigs}
            activeJobConfig={activeJobConfig}
            setActiveJobConfig={setActiveJobConfig}
            activeDetailPanel={activeDetailPanel}
            setActiveDetailPanel={setActiveDetailPanel}
            loadingFlowDiagram={loadingFlowDiagram}
          />
        </div>
        <div className="flex flex-col w-[40%] h-full border-l bg-neutral-0 border-l-neutral-200">
          <ResumeScreenDetailPanel
            selectedJobConfig={selectedJobConfig}
            jobStages={jobStages}
            loadingJobStages={loadingJobStages}
            greenhouseJobs={greenhouseJobs || []}
            loadingGreenhouseJobs={loadingGreenhouseJobs}
            allJobConfigs={allJobConfigs}
            openJobConfigTestDialog={openJobConfigTestDialog}
            activeDetailPanel={activeDetailPanel}
            activeJobConfig={activeJobConfig}
            setActiveJobConfig={setActiveJobConfig}
            setLoadingFlowDiagram={setLoadingFlowDiagram}
            isRecruitingScreeningEnabled={isRecruitingScreeningEnabled ?? false}
          />
        </div>
      </div>
      <GrowthCenterResumeScreeningTestModal
        open={testResumeScreeningDialogOpen}
        onClose={closeJobConfigTestDialog}
        jobConfig={activeJobConfig}
        updateAllJobConfigs={updateAllJobConfigs}
      />
    </div>
  );
};

export default ResumeScreenCreationOrUpdate;
